/**
 * BusAnalyticsPage — full analytics hub for school bus fleet admin.
 * 4 sub-tabs: Fuel | Routes | Drivers | Incidents.
 * Exact port of the Fleet Console mockup. All client-side (Firebase Spark).
 * Data-fetching hooks are unchanged from the original — only presentation
 * (the mockup layout + SVG charts) was rebuilt.
 */
import React, { useState, useEffect, useMemo } from "react";
import ErrorBoundary from './ErrorBoundary';
import { collection, query, where, onSnapshot, getDocs, limit, orderBy, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { openLatLng } from "../utils/locationCrypto";
import { splitRouteSegments } from "../utils/routeSegments";
import { AreaChartSVG, BarChartSVG, LineChartSVG, Donut, HBars, Series } from "./analyticsCharts";
import { GoogleMap, useJsApiLoader, Marker, Polyline } from "@react-google-maps/api";
import Zana from "./Zana";
import ZanaModeTab from "./ZanaModeTab";
import ZanaBand from "./ZanaBand";
import ZanaModeUpsell from "./ZanaModeUpsell";
import FuelPriceShelfCard from "./FuelPriceShelfCard";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION } from "../utils/googleMapsLoader";
import { parseYmd, termWeekKeys, activeTermStartYmd, liveTermStartYmd } from "../utils/termDates";
import { windowBounds, windowStats, sideOfRouteType, type RouteWindowKey, type RunLite, type WindowStats } from "../utils/routeWindow";
import "./BusAnalyticsPage.css";
import { loadTripTrailRaw } from "../utils/tripTrail";

// ─── Types ──────────────────────────────────────────────────────────────────
interface BusAnalyticsPageProps {
  buses: Array<{ id: string; plate: string; name: string; fuelTankCapacityL?: number | null; benchmarkKmPerL?: number | null }>;
  fleetOrgCode: string | null;
  onBack?: () => void;
  initialBusId?: string | null;
  /** Zana Mode (Premium/Enterprise only). When false, the Zana Mode tab shows the
   *  upsell "trailer" instead of the live briefing, and the per-tab ZANA bands hide. */
  zanaEnabled?: boolean;
  /** Opens the upgrade flow from the Zana Mode trailer (Basic orgs). */
  onUpgrade?: () => void;
  /** Phase 3 — Action Registry executor for ZanaBand chips. */
  onZanaIntent?: import("../services/zanaActions").ZanaIntentExecutor;
  liveUnreadAlertCount?: number;
  /** Sub-tab Zana wants open, so a takeover glowing `driverTable` lands on
   *  Drivers instead of narrating over the Fuel tab. Bump `subTabNonce` to
   *  re-apply the same value — the owner may have clicked away since. */
  subTab?: SubTab | null;
  subTabNonce?: number;
  /** Reports the tab the owner actually selected so Ask Zana can describe the
   *  visible analytics surface instead of only knowing "Analytics". */
  onSubTabChange?: (tab: SubTab) => void;
  currentTermStartDate?: string | null;
  currentTermEndDate?: string | null;
  nextTermStartDate?: string | null;
}
export type SubTab = "fuel" | "routes" | "drivers" | "incidents" | "advanced";

/** One bus, one side (morning/evening): the roads it actually drove, compared.
 *  Written nightly by engine/route/roadSweep.js. */
interface BusRoadsDoc {
  id: string; orgCode: string; busId: string; side: string;
  evidence: "none" | "thin" | "ok"; minutesApart: number | null; sentence: string;
  fastestRoadId: string | null; slowestRoadId: string | null; tripsConsidered: number;
  roads: { roadId: string; name?: string | null; nameAt?: { lat: number; lng: number } | null;
    trips: number; avgMinutes: number; avgStops: number; isLearned: boolean;
    crawlZones: { seenOnTrips: number; ofTrips: number; avgSeconds: number; hourLocal: number | null }[];
    anchor: { lat: number; lng: number }[] }[];
}

interface RouteProfileDoc { id: string; orgCode: string; busId: string; routeHash?: string; routeType?: string; startLabel: string; endLabel: string; startLat?: number; startLng?: number; endLat?: number; endLng?: number; avgDurationMinutes: number; avgDistanceKm?: number; tripCount: number; effectivenessScore: number; flags: string[]; flagReasons?: string[]; isLearned: boolean; lastSeen?: Timestamp | number | null; scoredAt?: Timestamp | number | null; recentDurations?: number[]; durationStdDev?: number; driverDurations?: Record<string, number[]>; }
interface RefuelEventDoc { id: string; busId: string; litersAdded?: number; litres?: number; liters?: number; costEstimate?: number; costKsh?: number; cost?: number; kmSinceLastRefuel?: number; actualEfficiencyKmPerL?: number; benchmarkKmPerL?: number; timestamp?: Timestamp | number | null; recordedAt?: Timestamp | number | null; createdAt?: Timestamp | number | null; orgCode?: string; }
interface DriverRatingDoc { id: string; driverId: string; orgCode: string; stars: number; safetyScore: number; fuelScore: number; complianceScore: number; coverageScore?: number; timeScore: number; deductions?: Array<{ reason: string; starsLost: number }>; flags?: string[]; totalTrips?: number; windowTrips?: number; activeIncidentFlags?: number; }
interface UserDoc { id: string; displayName?: string; name?: string; email?: string; role?: string; }
interface IssueReportDoc { id: string; busId?: string; issueType?: string; details?: string; timestamp?: Timestamp | number | null; createdAt?: Timestamp | number | null; driverId?: string; severity?: string; }

// ─── Helpers ──────────────────────────────────────────────────────────────────
/* Users are written with schoolCode, organizationCode OR orgCode depending on
   which surface created them (F12). Querying one spelling misses the drivers
   written with another — they render blank or unnamed. Union across all three
   and dedupe by id, the same drift the backend's orgEntities absorbs. */
const ORG_USER_FIELDS = ["schoolCode", "organizationCode", "orgCode"] as const;
async function getOrgUserDocs(fleetOrgCode: string) {
  const byId = new Map<string, any>();
  for (const field of ORG_USER_FIELDS) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const snap = await getDocs(query(collection(db, "users"), where(field, "==", fleetOrgCode)));
      snap.docs.forEach((d) => { if (!byId.has(d.id)) byId.set(d.id, d); });
    } catch { /* one spelling failing must not lose the others */ }
  }
  return [...byId.values()];
}

function tsToMs(v: Timestamp | number | null | undefined): number {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (v && typeof (v as Timestamp).toDate === "function") return (v as Timestamp).toDate().getTime();
  return 0;
}
function formatDate(ms: number): string { return ms ? new Date(ms).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"; }
function formatMinutes(m: number): string { if (!m) return "—"; const h = Math.floor(m / 60), min = Math.round(m % 60); return h === 0 ? `${min}m` : `${h}h ${min}m`; }
function weekKey(ms: number): string { const d = new Date(ms); const day = d.getDay() || 7; const monday = new Date(d); monday.setDate(d.getDate() - day + 1); return monday.toISOString().slice(0, 10); }
function refuelMs(e: RefuelEventDoc): number { return tsToMs(e.timestamp ?? e.recordedAt ?? e.createdAt ?? null); }
function refuelLiters(e: RefuelEventDoc): number { return Number(e.litersAdded ?? e.litres ?? e.liters ?? 0) || 0; }
function refuelCost(e: RefuelEventDoc): number { return Number(e.costEstimate ?? e.costKsh ?? e.cost ?? 0) || 0; }
function refuelKm(e: RefuelEventDoc): number { return Number(e.kmSinceLastRefuel ?? 0) || 0; }
function fmtKES(n: number): string { return `Ksh ${Math.round(n).toLocaleString("en-KE")}`; }
function fmtKEScompact(n: number): string {
  if (!n) return "0";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${Math.round(n)}`;
}
function starString(v: number): string { const full = Math.round(v); return "★".repeat(Math.max(0, full)) + "☆".repeat(Math.max(0, 5 - full)); }

/** Tiny inline trend line for a route's recent trip durations — oldest on the
 *  left, newest on the right. Dots mark each trip and a soft fill grounds the
 *  line, so it reads as a chart rather than a stray pen stroke (Gabriel,
 *  2026-08-24: "that line beside recent trips, what's up with that??"). */
function Sparkline({ values, color = "#2563eb", width = 150, height = 30 }: { values: number[]; color?: string; width?: number; height?: number }): JSX.Element | null {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const step = width / (values.length - 1);
  const xy = values.map((v, i) => ({ x: i * step, y: height - ((v - min) / span) * (height - 8) - 4 }));
  const pts = xy.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const r = values.length > 12 ? 1.4 : 2.2;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }} aria-hidden>
      <polygon points={`0,${height} ${pts} ${width},${height}`} fill={color} opacity={0.1} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
      {xy.map((p, i) => <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r={r} fill={color} />)}
    </svg>
  );
}
/* The Roads driven card deliberately has NO map: Gabriel tried one and asked
   for it to go — "just the way it was was fine, I just wanted road 1 and road
   2 to have names. That's it, no maps on them" (2026-08-24). The rows carry
   the reverse-geocoded names instead. */

const STALE_ROUTE_MS = 30 * 24 * 60 * 60 * 1000;
/* How many matching trips before a route is "learned". Mirrors the backend's
   LEARNED_AFTER_TRIPS (engine/route/reconcile.js, env ROUTE_LEARNED_AFTER_TRIPS,
   default 3). Kept as a single constant so the progress UI and the copy never
   drift the way the "~14 trips" text did. */
const ROUTE_LEARN_TRIPS = 3;
/** A learned route no one has driven in ~30 days is stale — its green score is no
 *  longer trustworthy, so we dim it and label it rather than implying "all good". */
function isStaleRoute(p: RouteProfileDoc): boolean {
  const ms = tsToMs(p.scoredAt ?? p.lastSeen ?? null);
  return ms > 0 && Date.now() - ms > STALE_ROUTE_MS;
}

const BUS_COLORS = ["#2563eb", "#0d9488", "#7c3aed", "#d97706", "#db2777", "#16a34a", "#ea580c", "#4f46e5"];
/** Trips before a route counts as learned. MUST match
 *  `engine/route/reconcile.js` LEARNED_AFTER_TRIPS and the mobile
 *  `BusRouteIntelligenceService.learnedAfterTrips`. The page said "14" long
 *  after both engines moved to 3, which is why the label and the engine
 *  disagreed on Gabriel's screen. */
const LEARNED_AFTER_TRIPS = 3;

const FUEL_ICON = "M3 3h11v18H3V3Zm14 3 4 4v9a2 2 0 0 1-4 0v-6h-1V6h1Z";
const UP = "m12 6 7 8h-4v4h-6v-4H5l7-8Z";
const DOWN = "m12 18-7-8h4V6h6v4h4l-7 8Z";

function Icon({ d }: { d: string }) { return <svg viewBox="0 0 24 24"><path d={d} /></svg>; }
function Delta({ dir, children }: { dir: "up" | "down" | "flat"; children: React.ReactNode }) {
  return <div className={`kpi-d ${dir}`}>{dir !== "flat" && <Icon d={dir === "up" ? UP : DOWN} />}{children}</div>;
}
function busColor(buses: BusAnalyticsPageProps["buses"], id: string): string {
  const i = buses.findIndex((b) => b.id === id);
  return BUS_COLORS[(i < 0 ? 0 : i) % BUS_COLORS.length];
}

// ─── FUEL TAB ───────────────────────────────────────────────────────────────
/** tier 'basic' (pilot) hides fuel-efficiency (km/L) behind a Premium lock; tier
 *  'full' (paid, inside Analytics) shows everything. onUpgrade opens the upgrade flow. */
function FuelTab({
  buses,
  fleetOrgCode,
  rangeDays,
  tier = "full",
  onUpgrade,
  isZanaTier = false,
  termStartDate = null,
  termEndDate = null,
}: {
  buses: BusAnalyticsPageProps["buses"];
  fleetOrgCode: string | null;
  rangeDays: number;
  tier?: "basic" | "full";
  onUpgrade?: () => void;
  /** Premium/Enterprise → EPRA test button; Basic → Places station upload. */
  isZanaTier?: boolean;
  termStartDate?: string | null;
  termEndDate?: string | null;
}) {
  const [events, setEvents] = useState<RefuelEventDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const ninetyDaysAgo = useMemo(() => Date.now() - 90 * 24 * 60 * 60 * 1000, []);
  const busIdKey = buses.map((b) => b.id).sort().join(",");
  const shownFuelOnce = React.useRef(false);

  useEffect(() => {
    if (!fleetOrgCode || !busIdKey) { setLoading(false); return; }
    if (!shownFuelOnce.current) setLoading(true);
    const busIds = busIdKey.split(",").filter(Boolean);
    const chunks: string[][] = [];
    // 8 per chunk, not 30 — the busRefuelEvents rule is get(buses/{id})-based
    // like busIssueReports; same access-call-budget caution (bug 73).
    for (let i = 0; i < busIds.length; i += 8) chunks.push(busIds.slice(i, i + 8));
    const allUnsubs: (() => void)[] = [];
    let fetched: RefuelEventDoc[] = [];
    const seen = new Set<number>();
    chunks.forEach((chunk, idx) => {
      const q = query(collection(db, "busRefuelEvents"), where("busId", "in", chunk));
      const unsub = onSnapshot(q, (snap) => {
        const chunkDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() } as RefuelEventDoc)).filter((e) => refuelMs(e) >= ninetyDaysAgo);
        fetched = [...fetched.filter((e) => !chunk.includes(e.busId ?? "")), ...chunkDocs];
        seen.add(idx);
        if (seen.size >= chunks.length) {
          shownFuelOnce.current = true;
          setEvents([...fetched]);
          setLoading(false);
        }
      }, () => {
        seen.add(idx);
        if (seen.size >= chunks.length) setLoading(false);
      });
      allUnsubs.push(unsub);
    });
    return () => allUnsubs.forEach((u) => u());
    // busIdKey, not `buses`: a new array every parent render was tearing the
    // listener down before the first snapshot, so the tab stuck on "Loading fuel data…".
  }, [fleetOrgCode, busIdKey, ninetyDaysAgo]);

  const cutoff = useMemo(() => {
    const from = parseYmd(activeTermStartYmd(termStartDate));
    if (from) return from.getTime();
    return Date.now() - rangeDays * 24 * 60 * 60 * 1000;
  }, [rangeDays, termStartDate]);
  /* LIKE FOR LIKE — the previous window is the same LENGTH as the current one,
     ending exactly where it begins.

     It used to be `now - 2 * rangeDays` regardless, while `cutoff` follows the
     TERM when a term calendar exists. So a 60-day term was compared against
     whatever happened to sit between 60 days ago and the term start — a window
     of a different length, and frequently of ZERO length, because those two
     dates are often the same day. The arrows were then either meaningless or
     absent, under a label that promised a straight comparison (review, 09-06;
     Gabriel's call to fix the maths rather than the wording).

     Honesty about data we do not have: events are only loaded for the last 90
     days, so if the matching previous window reaches further back than that we
     would be comparing a full period against a truncated one and calling the
     difference real. In that case there is no delta to show. */
  const curSpanMs = useMemo(() => Math.max(0, Date.now() - cutoff), [cutoff]);
  const prevCutoff = useMemo(() => cutoff - curSpanMs, [cutoff, curSpanMs]);
  const prevWindowFullyLoaded = prevCutoff >= ninetyDaysAgo;
  const cur = useMemo(() => events.filter((e) => refuelMs(e) >= cutoff), [events, cutoff]);
  const prev = useMemo(
    () => (prevWindowFullyLoaded
      ? events.filter((e) => refuelMs(e) >= prevCutoff && refuelMs(e) < cutoff)
      : []),
    [events, prevCutoff, cutoff, prevWindowFullyLoaded],
  );

  const agg = (evts: RefuelEventDoc[]) => {
    let spend = 0, litres = 0, km = 0, effSum = 0, effN = 0;
    evts.forEach((e) => { spend += refuelCost(e); litres += refuelLiters(e); km += refuelKm(e); const ef = Number(e.actualEfficiencyKmPerL ?? 0); if (ef > 0) { effSum += ef; effN++; } });
    return { spend, litres, km, eff: effN ? effSum / effN : 0, cpk: km > 0 ? spend / km : 0 };
  };
  const A = useMemo(() => agg(cur), [cur]);
  const P = useMemo(() => agg(prev), [prev]);
  const pct = (a: number, b: number) => (b > 0 ? Math.round(((a - b) / b) * 1000) / 10 : null);
  const spendDelta = prev.length ? pct(A.spend, P.spend) : null;
  const cpkDelta = prev.length && P.cpk > 0 ? pct(A.cpk, P.cpk) : null;

  const weeks = useMemo(() => {
    if (termStartDate) {
      const liveStart = activeTermStartYmd(termStartDate);
      if (liveStart) {
        const today = new Date().toISOString().slice(0, 10);
        const toYmd = termEndDate && termEndDate < today ? termEndDate : today;
        const keys = termWeekKeys(liveStart, toYmd);
        if (keys.length) return keys;
      }
    }
    const set = new Set<string>();
    cur.forEach((e) => { const ms = refuelMs(e); if (ms) set.add(weekKey(ms)); });
    return [...set].sort();
  }, [cur, termStartDate, termEndDate]);
  /* Keep each series attached to the bus it belongs to.
   *
   * This used to return a bare Series[] and drop the bus, so the legend below
   * had to guess which buses survived the filter — it tested `areaSeries[i]`
   * against the UNFILTERED bus index, which is only ever true for the first N
   * buses in the list. On yes.bus that labelled the chart "KAA123A, KBZ789C"
   * while it was plotting KCA123A and KDB456B, whose spend the donut beside it
   * reported correctly. Two fuel charts on one screen, naming four different
   * buses. The colours were mismatched by the same off-by-filter: assigned
   * here from the pre-filter index and re-derived there from the post-filter
   * one. Carrying the bus through the filter makes both impossible. */
  const areaEntries = useMemo(() => buses.map((b, i) => {
    const byWeek: Record<string, number> = {};
    cur.filter((e) => e.busId === b.id).forEach((e) => { const wk = weekKey(refuelMs(e)); byWeek[wk] = (byWeek[wk] ?? 0) + refuelCost(e); });
    return { bus: b, color: BUS_COLORS[i % BUS_COLORS.length], data: weeks.map((w) => byWeek[w] ?? 0) };
  }).filter((s) => s.data.some((v) => v > 0)), [buses, cur, weeks]);
  const areaSeries: Series[] = useMemo(
    () => areaEntries.map(({ color, data }) => ({ color, data })),
    [areaEntries],
  );
  const areaWeekLabels = weeks.map((_, i) => `W${i + 1}`);

  const spendByBus = useMemo(() => {
    const map: Record<string, number> = {};
    cur.forEach((e) => { map[e.busId ?? ""] = (map[e.busId ?? ""] ?? 0) + refuelCost(e); });
    return buses.map((b) => ({ bus: b, cost: Math.round(map[b.id] ?? 0) })).filter((x) => x.cost > 0).sort((a, b) => b.cost - a.cost);
  }, [buses, cur]);

  const effByBus = useMemo(() => {
    const a: Record<string, { km: number; l: number; eS: number; eN: number }> = {};
    cur.forEach((e) => {
      const id = e.busId ?? "";
      const o = (a[id] ??= { km: 0, l: 0, eS: 0, eN: 0 });
      /* PAIRED fills only — the same rule the bus page's ring uses. A first
         fill has no previous odometer (km 0) but real litres; counting its
         litres against nobody's km dragged 999Z from 4.89 to 3.9 km/L while
         the ring said 4.89 (Gabriel, 2026-08-24: "how can I explain it?"). */
      const km = refuelKm(e);
      if (km > 0) { o.km += km; o.l += refuelLiters(e); }
      const ef = Number(e.actualEfficiencyKmPerL ?? 0);
      if (ef > 0) { o.eS += ef; o.eN++; }
    });
    const rows = buses.map((b) => {
      const o = a[b.id] ?? { km: 0, l: 0, eS: 0, eN: 0 };
      const kmL = o.km > 0 && o.l > 0 ? o.km / o.l : o.eN ? o.eS / o.eN : null;
      return { bus: b, kmL, bench: b.benchmarkKmPerL ?? null, km: o.km, l: o.l };
    });
    /* WORST first. The card is titled "Fuel efficiency by bus" and carries the
       `barWorst` spotlight hook, and Zana's line beside it names the bus with
       the biggest gap — but sorting best-first meant the first, visually
       dominant bar was the opposite bus.

       Same honesty rule as the bus page's ring (estimateFuelEfficiency): a
       figure below a QUARTER of the bus's own benchmark is not fuel economy,
       it is a short km side (untracked trips, test litres). The ring refuses
       to score it, so this card must not paint it as a red bar either — it
       did (KCE998Z 1.6 km/L in red while the ring said "Need data"; Gabriel,
       2026-08-23). Buses with no litres this period are NAMED below the bars
       instead of silently missing (KCE999Z was just absent). */
    const bars = rows
      .filter((x) => x.kmL != null
        && x.kmL >= PLAUSIBLE_KM_PER_L.min && x.kmL <= PLAUSIBLE_KM_PER_L.max
        && !(x.bench && x.kmL < x.bench * 0.25))
      .sort((a2, b2) => (a2.kmL ?? 0) - (b2.kmL ?? 0));
    const notes = rows.filter((x) => !bars.includes(x)).map((x) => {
      const name = x.bus.name || x.bus.plate;
      if (x.kmL == null) return `${name} — no refuel with litres logged this period, so no km/L yet.`;
      if (x.bench && x.kmL < x.bench * 0.25) {
        return `${name} — ${x.l.toFixed(0)} L against ${x.km.toFixed(0)} km (${x.kmL.toFixed(1)} km/L vs a ${x.bench} km/L target) is not a real reading: the km side is incomplete (untracked trips or test litres). Not scored until it is.`;
      }
      return `${name} — ${x.kmL.toFixed(1)} km/L is outside what any bus does; not scored.`;
    });
    return { bars, notes };
  }, [buses, cur]);

  const shelf = fleetOrgCode ? (
    <FuelPriceShelfCard fleetOrgCode={fleetOrgCode} isZanaTier={isZanaTier} />
  ) : null;

  if (loading && events.length === 0) {
    return (
      <>
        {shelf}
        <div className="an-loading">Loading fuel data…</div>
      </>
    );
  }
  if (events.length === 0) {
    return (
      <>
        {shelf}
        <div className="card card-pad">
          <div className="an-empty">
            <div className="t">{activeTermStartYmd(termStartDate) ? "No fuel events this term yet" : "No fuel events in the last 90 days"}</div>
            Fuel data appears here once drivers log refuel events.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {shelf}
      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi-k"><span className="gi" style={{ background: "var(--blue-bg)" }}><svg viewBox="0 0 24 24" fill="#2563eb"><path d={FUEL_ICON} /></svg></span>Total spend</div>
          <div className="kpi-v"><small>Ksh </small>{Math.round(A.spend).toLocaleString("en-KE")}</div>
          {spendDelta != null ? <Delta dir={spendDelta > 0 ? "up" : "down"}>{Math.abs(spendDelta)}% vs prev {rangeDays}d</Delta> : <div className="kpi-d flat">this period</div>}
        </div>
        {tier === "full" ? (
          <div className="kpi">
            <div className="kpi-k"><span className="gi" style={{ background: "var(--green-bg)" }}><svg viewBox="0 0 24 24" fill="#16a34a"><path d={FUEL_ICON} /></svg></span>Avg efficiency</div>
            <div className="kpi-v">{A.eff ? A.eff.toFixed(1) : "—"}<small> km/L</small></div>
            {P.eff > 0 && A.eff > 0 ? <Delta dir={A.eff >= P.eff ? "up" : "down"}>{Math.abs(A.eff - P.eff).toFixed(1)} {A.eff >= P.eff ? "better" : "worse"}</Delta> : <div className="kpi-d flat">avg of each refill</div>}
          </div>
        ) : (
          <div className="kpi" onClick={onUpgrade} style={{ cursor: "pointer", background: "linear-gradient(160deg,#1e3a8a,#312e81)", borderColor: "transparent" }} title="Premium feature">
            <div className="kpi-k" style={{ color: "#c7d2fe" }}><span className="gi" style={{ background: "rgba(255,255,255,.16)" }}>🔒</span>Avg efficiency</div>
            <div className="kpi-v" style={{ color: "#fff", fontSize: 18 }}>Premium</div>
            <div className="kpi-d" style={{ color: "#93c5fd" }}>Unlock fuel efficiency →</div>
          </div>
        )}
        <div className="kpi">
          <div className="kpi-k"><span className="gi" style={{ background: "var(--amber-bg)" }}><svg viewBox="0 0 24 24" fill="#d97706"><path d={FUEL_ICON} /></svg></span>Litres used</div>
          <div className="kpi-v">{Math.round(A.litres).toLocaleString("en-KE")}<small> L</small></div>
          <div className="kpi-d flat">across {spendByBus.length || buses.length} buses</div>
        </div>
        {/* data-zt: the fuel takeover glows `kpiCpk` on the Analytics tab. The
            only other one lives on the Cost estimate overlay, which she never
            opens — so without this hook that half of the step was a no-op. */}
        <div className="kpi" data-zt="kpiCpk">
          <div className="kpi-k"><span className="gi" style={{ background: "#efe9ff" }}><svg viewBox="0 0 24 24" fill="#7c3aed"><path d={FUEL_ICON} /></svg></span>Cost / km</div>
          <div className="kpi-v"><small>Ksh </small>{A.cpk ? Math.round(A.cpk) : "—"}</div>
          {cpkDelta != null ? <Delta dir={cpkDelta > 0 ? "up" : "down"}>{Math.abs(cpkDelta)}% {cpkDelta > 0 ? "pricier" : "cheaper"}</Delta> : <div className="kpi-d flat">per km</div>}
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="card card-pad">
          <div className="card-head"><div className="card-title">Weekly fuel cost</div><div className="card-note">{termStartDate ? "This term · Ksh" : `Last ${rangeDays} days · Ksh`}</div></div>
          {areaSeries.length > 0 ? <AreaChartSVG series={areaSeries} labels={areaWeekLabels} fmt={(v) => fmtKEScompact(v)} /> : <div className="an-empty">Not enough data yet.</div>}
          <div className="legend">{areaEntries.map((s) => <span key={s.bus.id}><i style={{ background: s.color }} />{s.bus.name || s.bus.plate}</span>)}</div>
        </div>
        <div className="card card-pad">
          <div className="card-head"><div className="card-title">Spend by bus</div></div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 0 4px" }}>
            <Donut data={spendByBus.map((s) => ({ value: s.cost, color: busColor(buses, s.bus.id) }))} center={fmtKEScompact(spendByBus.reduce((a, s) => a + s.cost, 0))} sub="Ksh spend" />
          </div>
          <div className="legend" style={{ justifyContent: "center" }}>{spendByBus.map((s) => <span key={s.bus.id}><i style={{ background: busColor(buses, s.bus.id) }} />{s.bus.name || s.bus.plate} · {fmtKES(s.cost)}</span>)}</div>
        </div>
      </div>

      {tier === "full" ? (
        <div data-zt="barWorst" className="card card-pad">
          <div className="card-head"><div className="card-title">Fuel efficiency by bus</div><div className="card-note">km / litre · actual vs benchmark</div></div>
          <div style={{ marginTop: 6 }}>
            {effByBus.bars.length > 0 ? (
              <HBars
                data={effByBus.bars.map((e) => {
                  const ratio = e.bench && e.kmL ? e.kmL / e.bench : 1;
                  const color = ratio >= 0.85 ? "#16a34a" : ratio >= 0.7 ? "#d97706" : "#dc2626";
                  return { label: e.bus.name || e.bus.plate, value: Number((e.kmL ?? 0).toFixed(1)), color };
                })}
                fmt={(v) => `${v.toFixed(1)} km/L`}
              />
            ) : (
              <div style={{ fontSize: 13, color: "var(--ink-3)" }}>No bus has a scoreable km/L this period yet.</div>
            )}
            {effByBus.notes.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.6 }}>
                {effByBus.notes.map((n, i) => <div key={i}>· {n}</div>)}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-pad" onClick={onUpgrade} style={{ cursor: "pointer", background: "linear-gradient(160deg,#1e3a8a,#312e81)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 16, fontWeight: 800 }}><span>🔒</span>Fuel efficiency by bus</div>
            <div style={{ fontSize: 13, color: "#c7d2fe", marginTop: 6, maxWidth: 460, lineHeight: 1.5 }}>See each bus’s real km/L against an age-adjusted benchmark, spot thirsty engines, and track cost-per-km trends. Included with Premium.</div>
          </div>
          <button onClick={onUpgrade} style={{ padding: "10px 18px", borderRadius: 11, border: "none", background: "#fff", color: "#1e3a8a", fontWeight: 700, fontSize: 13.5, cursor: "pointer", whiteSpace: "nowrap" }}>Unlock with Premium →</button>
        </div>
      )}
    </>
  );
}

/** Standalone basic Fuel screen for pilots — same data as the Analytics Fuel tab
 *  but with efficiency locked. Mounted from the pilot-only top-nav "Fuel" item. */
export function FuelOverview({ buses, fleetOrgCode, onUpgrade }: { buses: BusAnalyticsPageProps["buses"]; fleetOrgCode: string | null; onUpgrade?: () => void }): JSX.Element {
  const [rangeDays, setRangeDays] = useState(90);
  return (
    <div className="pear-analytics">
      <div className="an-head">
        <div>
          <div className="an-title">Fuel</div>
          <div className="an-sub">{buses.length} bus{buses.length !== 1 ? "es" : ""} · {fleetOrgCode ?? "Unknown org"} · spend overview</div>
        </div>
        <div className="an-range">
          {[7, 30, 90].map((d) => <button key={d} className={rangeDays === d ? "active" : ""} onClick={() => setRangeDays(d)}>{d}d</button>)}
        </div>
      </div>
      <div className="an-body">
        <FuelTab buses={buses} fleetOrgCode={fleetOrgCode} rangeDays={rangeDays} tier="basic" onUpgrade={onUpgrade} isZanaTier={false} />
      </div>
    </div>
  );
}

// ─── ROUTES TAB ─────────────────────────────────────────────────────────────
function scoreBadge(s: number): [string, string] {
  return s >= 80 ? ["sb-good", "Effective"] : s >= 60 ? ["sb-watch", "Watch"] : s >= 40 ? ["sb-concern", "Concern"] : ["sb-bad", "Ineffective"];
}

// 4-tier route health colour. Green = route is paying off, red = needs review.
type RouteHealth = { color: string; label: string; hint: string };
function routeHealth(s: number): RouteHealth {
  if (s >= 80) return { color: "#16a34a", label: "Healthy", hint: "Running well — not losing money on this route" };
  if (s >= 60) return { color: "#eab308", label: "Alright", hint: "Mostly fine, keep an eye on it" };
  if (s >= 40) return { color: "#f97316", label: "Slightly bad", hint: "Drifting — worth a look soon" };
  return { color: "#dc2626", label: "Concern", hint: "Check this route and ask the drivers" };
}

type LatLng = { lat: number; lng: number };
/** Precise endpoints if stored, else parse them back out of the routeHash geocells. */
function routeCoords(p: RouteProfileDoc): { start: LatLng; end: LatLng } | null {
  const ok = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
  if (ok(p.startLat) && ok(p.startLng) && ok(p.endLat) && ok(p.endLng)) {
    return { start: { lat: p.startLat, lng: p.startLng }, end: { lat: p.endLat, lng: p.endLng } };
  }
  if (p.routeHash) {
    const [s, e] = p.routeHash.split("__");
    const sc = (s ?? "").split("_").map(Number);
    const ec = (e ?? "").split("_").map(Number);
    if (sc.length === 2 && ec.length === 2 && sc.every(Number.isFinite) && ec.every(Number.isFinite)) {
      return { start: { lat: sc[0], lng: sc[1] }, end: { lat: ec[0], lng: ec[1] } };
    }
  }
  return null;
}
/** Human-legible route name.
 *
 *  Two things this used to get wrong, both on Gabriel's screen 2026-08-18:
 *   • An unnamed endpoint fell back to `road @ -1.292, 36.769`. Coordinates
 *     are never a name a customer should read; the geocoder simply had not
 *     resolved that spot yet (it retries on the next completed trip).
 *   • Both ends geocoded to the same town produced "Nairobi → Nairobi" — true
 *     of every school run in the county, and it tells the owner nothing.
 *  Now: an unresolved end says so plainly, and a same-place pair collapses to
 *  one name with the shape of the run ("Nairobi · out and back"). */
function routeName(p: RouteProfileDoc): string {
  const start = (p.startLabel ?? "").trim();
  const end = (p.endLabel ?? "").trim();
  if (start && end && start.toLowerCase() === end.toLowerCase()) {
    return `${start} · out and back`;
  }
  const side = (t: string) => t || "unnamed stop";
  return `${side(start)} → ${side(end)}`;
}

/** MUST match BusService.kMinRouteLearnKm / reconcile MIN_ROUTE_LEARN_KM. */
const MIN_ROUTE_LEARN_KM = 0.5;
/** MUST match bus-page efficiency guard — sub-0.5 or >40 km/L is junk, not a bar. */
const PLAUSIBLE_KM_PER_L = { min: 0.5, max: 40 };

/* "trip" (2026-09-29): planned school trips are learned under their own
   cluster key now (route analytics item 3) and get their own section. */
type RouteSide = "morning" | "evening" | "trip" | "other";

function routeSide(p: RouteProfileDoc): RouteSide {
  const t = (p.routeType ?? "").toLowerCase();
  if (t.includes("school_trip")) return "trip";
  if (t.includes("evening") || t.includes("return")) return "evening";
  if (t.includes("morning") || t.includes("outbound")) return "morning";
  return "other";
}

function routeSideLabel(side: RouteSide): string {
  if (side === "morning") return "Morning";
  if (side === "evening") return "Evening";
  if (side === "trip") return "School trip";
  return "Other";
}

/** Hide desk-test junk (0 km · 3 min profiles) from the Routes UI. */
function isCredibleRoute(p: RouteProfileDoc): boolean {
  const km = p.avgDistanceKm ?? 0;
  const dur = p.avgDurationMinutes ?? 0;
  if (km >= MIN_ROUTE_LEARN_KM) return true;
  /* Legacy profiles may predate avgDistanceKm — keep if duration looks real. */
  if (km <= 0 && dur >= 5 && p.tripCount >= 1) return true;
  return false;
}

function routeDisplayName(p: RouteProfileDoc): string {
  const base = routeName(p);
  const side = routeSide(p);
  if (side === "other") return base;
  return `${routeSideLabel(side)} · ${base}`;
}

function formatRouteKm(km: number | undefined | null): string {
  return (km ?? 0) > 0 ? `${km!.toFixed(1)} km` : "—";
}

const ROUTE_SIDES: RouteSide[] = ["morning", "evening", "trip", "other"];

/** Live map of every learned route, each pinned + coloured by health. Click a pin
 *  (or a table row) to focus it; the map pans and the start/end are drawn. */
function RoutesMap({ profiles, selectedId, onSelect }: { profiles: RouteProfileDoc[]; selectedId: string | null; onSelect: (id: string) => void }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const pins = useMemo(() => profiles.map((p) => ({ p, c: routeCoords(p) })).filter((x): x is { p: RouteProfileDoc; c: { start: LatLng; end: LatLng } } => x.c !== null), [profiles]);
  const selected = useMemo(() => pins.find((x) => x.p.id === selectedId) ?? null, [pins, selectedId]);

  // Fit all pins on first load / when the set changes.
  useEffect(() => {
    if (!map || pins.length === 0 || !window.google) return;
    const b = new window.google.maps.LatLngBounds();
    pins.forEach((x) => { b.extend(x.c.start); b.extend(x.c.end); });
    map.fitBounds(b, 64);
  }, [map, pins]);

  // Pan to a selected route.
  useEffect(() => {
    if (map && selected) map.panTo(selected.c.start);
  }, [map, selected]);

  /* THE ROUTE MAP NOW DRAWS THE ROUTE.
     It used to draw one straight line from the start pin to the end pin — and
     a school run is a loop that comes back to nearly where it began, so a
     3.6 km drive rendered as a ten-metre stroke (Gabriel, 2026-08-18: "the
     length of that line is barely ten metres on actual ground"). The profile
     document holds only the two anchors, so the trail is read from the most
     recent completed trip of this bus and route type: `busAttendance` keeps
     `routePoints`, and fleet admins can read it. One query per selection, not
     per render; sealed points are opened with the same helper the live map
     uses. When no trail exists the straight line stays, and says so. */
  const [trail, setTrail] = useState<{ forId: string; path: { lat: number; lng: number; gapBefore?: boolean }[] } | null>(null);
  useEffect(() => {
    if (!selected) { setTrail(null); return; }
    const forId = selected.p.id;
    let cancelled = false;
    (async () => {
      try {
        const rt = (selected.p.routeType ?? "").trim().toLowerCase();
        const q = query(
          collection(db, "busAttendance"),
          where("busId", "==", selected.p.busId),
          orderBy("routeStartedAt", "desc"),
          limit(24),
        );
        const snap = await getDocs(q);
        /* Pick the DENSEST matching trail, not merely the newest: the route
           card once drew a sparse 30-point trip (a diagonal "cut" across
           blocks) while 47–62-point trips of the same route sat right behind
           it (Gabriel, 2026-08-24). More stored points = a truer line. */
        const docsByDensity = [...snap.docs].sort((da, db) => {
          const a = da.data() as Record<string, unknown>;
          const b = db.data() as Record<string, unknown>;
          const sLen = (x: Record<string, unknown>) => (Array.isArray(x.routePointsSnapped) ? x.routePointsSnapped.length : 0);
          // Spilled points count toward density (bug 216): a long run keeps
          // only its last stretch in the array.
          const rLen = (x: Record<string, unknown>) => (Array.isArray(x.routePoints) ? x.routePoints.length : 0) + Number(x.trailSpilled ?? 0);
          return (sLen(b) - sLen(a)) || (rLen(b) - rLen(a));
        });
        for (const d of docsByDensity) {
          const data = d.data() as Record<string, unknown>;
          const dataRt = String(data.routeType ?? "").trim().toLowerCase();
          if (rt && dataRt && dataRt !== rt) continue;
          const raw = Array.isArray(data.routePoints) ? (data.routePoints as unknown[]) : [];
          if (raw.length < 2) continue;
          const km = Number(data.distanceKm ?? 0);
          if (km > 0 && km < MIN_ROUTE_LEARN_KM) continue;
          /* The reconcile's road-snapped line (plain lat/lng) beats raw GPS
             scatter — "you can clearly see how bad our tracking is"
             (Gabriel, 2026-08-24). Falls back to decrypting the raw trail.
             gapBefore survives BOTH ways, so a killed-app hole is a lifted
             pen, never a straight cut across the map. */
          const snappedRaw = Array.isArray(data.routePointsSnapped) ? (data.routePointsSnapped as Record<string, unknown>[]) : [];
          const snappedPath = snappedRaw
            .map((p) => ({ lat: Number(p.lat), lng: Number(p.lng), gapBefore: p.gapBefore === true }))
            .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
          if (snappedPath.length >= 2) {
            if (!cancelled) setTrail({ forId, path: snappedPath });
            return;
          }
          // The whole run, chunks included (bug 216).
          const wholeRaw = Number(data.trailChunks ?? 0) > 0
            ? await loadTripTrailRaw(db, "busAttendance", d.id, data as { routePoints?: unknown[]; trailChunks?: number; trailWindowStart?: number | null })
            : raw;
          const opened = await Promise.all(wholeRaw.map((pt) => openLatLng(pt)));
          const path: { lat: number; lng: number; gapBefore?: boolean }[] = [];
          opened.forEach((x, i) => {
            if (!x) return;
            const rawPt = wholeRaw[i] as Record<string, unknown> | null;
            path.push({ lat: x.lat, lng: x.lng, gapBefore: !!rawPt && rawPt.gapBefore === true });
          });
          if (path.length >= 2) {
            if (!cancelled) setTrail({ forId, path });
            return;
          }
        }
        if (!cancelled) setTrail({ forId, path: [] });
      } catch {
        if (!cancelled) setTrail({ forId, path: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);
  const trailPath = trail && selected && trail.forId === selected.p.id && trail.path.length >= 2 ? trail.path : null;

  // Fit to the drawn trail once one arrives — the two anchors of a loop sit
  // metres apart, so fitting to them alone leaves the real drive off-screen.
  useEffect(() => {
    if (!map || !trailPath || !window.google) return;
    const b = new window.google.maps.LatLngBounds();
    trailPath.forEach((pt) => b.extend(pt));
    map.fitBounds(b, 48);
  }, [map, trailPath]);


  if (!apiKey.trim()) {
    return <div className="rt-map-missing">Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to show routes on the map. The table below works without it.</div>;
  }
  if (!isLoaded) return <div className="rt-map-loading">Loading map…</div>;
  if (pins.length === 0) return <div className="rt-map-missing">Route locations aren’t available yet — they appear as drivers complete more trips.</div>;

  return (
    <div className="rt-map">
      <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} onLoad={setMap} options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, clickableIcons: false }}>
        {/* One polyline per gap-free segment — a killed-app hole is a lifted
            pen, never a straight cut across the map (Gabriel, 2026-08-24). */}
        {selected && trailPath && splitRouteSegments(trailPath).map((seg, i) => (
          seg.length >= 2 ? (
            <Polyline
              key={`trail-${i}`}
              path={seg}
              options={{
                strokeColor: routeHealth(selected.p.effectivenessScore).color,
                strokeOpacity: 0.9,
                strokeWeight: 4,
              }}
            />
          ) : null
        ))}
        {selected && !trailPath && (
          <Polyline
            path={[selected.c.start, selected.c.end]}
            options={{
              strokeColor: routeHealth(selected.p.effectivenessScore).color,
              strokeOpacity: 0.45,
              strokeWeight: 4,
            }}
          />
        )}
        {/* Start / end dots on the focused route (Gabriel, 2026-08-24). */}
        {selected && trailPath && (
          <>
            <Marker
              position={trailPath[0]}
              zIndex={3}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 6.5,
                fillColor: "#ffffff",
                fillOpacity: 1,
                strokeColor: routeHealth(selected.p.effectivenessScore).color,
                strokeWeight: 3.5,
              }}
            />
            <Marker
              position={trailPath[trailPath.length - 1]}
              zIndex={3}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 6.5,
                fillColor: routeHealth(selected.p.effectivenessScore).color,
                fillOpacity: 1,
                strokeColor: "#ffffff",
                strokeWeight: 3,
              }}
            />
          </>
        )}
        {pins.map(({ p, c }) => {
          const h = routeHealth(p.effectivenessScore);
          const isSel = p.id === selectedId;
          const learning = !p.isLearned;
          return (
            <Marker
              key={p.id}
              position={c.start}
              title={`${routeName(p)} · ${p.isLearned ? `${p.effectivenessScore} (${h.label})` : `${p.tripCount}/${LEARNED_AFTER_TRIPS} learning`}`}
              onClick={() => onSelect(p.id)}
              icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: isSel ? 12 : 8, fillColor: learning ? "#9ca3af" : h.color, fillOpacity: learning ? 0.55 : 1, strokeColor: "#fff", strokeWeight: isSel ? 3 : 2 }}
              zIndex={isSel ? 999 : undefined}
            />
          );
        })}
        {selected && <Marker position={selected.c.end} title={`${selected.p.endLabel || "Destination"}`} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#111827", fillOpacity: 0.85, strokeColor: "#fff", strokeWeight: 2 }} />}
      </GoogleMap>
      <div className="rt-legend">
        {[80, 60, 40, 0].map((s) => { const h = routeHealth(s); return <span key={s} className="rt-legend-item"><i style={{ background: h.color }} />{h.label}</span>; })}
      </div>
    </div>
  );
}
/** Exported so the Trasse Families console reuses this verbatim instead of
 *  forking it. A household's routes are the same question as a fleet's — which
 *  way home actually works — so the table, the scores and the crawl-zone
 *  narration are identical. Behaviour here is unchanged. */
/** One completed run, as the window counter needs it. Null for anything that
 *  is not a finished school run (open, cancelled, a refuel drive). Endpoints:
 *  the learner's own `ri*` fields first, then the map pins the bus page uses,
 *  then the first/last breadcrumb. */
function runLiteFromDoc(id: string, data: Record<string, unknown>): RunLite | null {
  if (data.status !== "completed") return null;
  if (data.isRefuelTrip === true || data.routeType === "refuel") return null;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const ts = (v: unknown): Date | null => (v && typeof (v as Timestamp).toDate === "function" ? (v as Timestamp).toDate() : null);
  const pts = Array.isArray(data.routePoints) ? (data.routePoints as Array<{ lat?: unknown; lng?: unknown }>) : [];
  const first = pts[0];
  const last = pts[pts.length - 1];
  return {
    id,
    busId: String(data.busId || ""),
    side: sideOfRouteType(typeof data.routeType === "string" ? data.routeType : null),
    startedAt: ts(data.routeStartedAt),
    finishedAt: ts(data.routeFinishedAt),
    minutes: num(data.durationMinutes),
    km: num(data.distanceKm),
    sLat: num(data.riStartLat) ?? num(data.mapStartLat) ?? (first ? num(first.lat) : null),
    sLng: num(data.riStartLng) ?? num(data.mapStartLng) ?? (first ? num(first.lng) : null),
    eLat: num(data.riEndLat) ?? num(data.mapEndLat) ?? (last ? num(last.lat) : null),
    eLng: num(data.riEndLng) ?? num(data.mapEndLng) ?? (last ? num(last.lng) : null),
    driverId: typeof data.driverId === "string" ? data.driverId : null,
  };
}

interface PinnedRun { id: string; busId: string; name: string; date: Date | null; type: string; km: number | null; minutes: number | null }

export function RoutesTab({ buses, fleetOrgCode, termStartDate = null, termEndDate = null, onOpenRun }: { buses: BusAnalyticsPageProps["buses"]; fleetOrgCode: string | null; termStartDate?: string | null; termEndDate?: string | null; onOpenRun?: (busId: string, runId: string) => void }) {
  const [profiles, setProfiles] = useState<RouteProfileDoc[]>([]);
  const [roadDocs, setRoadDocs] = useState<BusRoadsDoc[]>([]);
  /* WINDOWS (2026-09-29, Gabriel: an admin could not ask "this term" or "last
     30 days" on Routes). The score stays the route's all-time figure — that is
     what "learned" means; the window re-counts runs, minutes and km from the
     bus's own completed runs inside the chosen dates. Opens on the term when
     the org has one (as Fuel does), all time otherwise. */
  const [windowKey, setWindowKey] = useState<RouteWindowKey>(() => (activeTermStartYmd(termStartDate) ? "term" : "all"));
  const [runs, setRuns] = useState<RunLite[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  /* PINNED RUNS (2026-09-29, item 2): runs the office named on the bus page
     ("the Tsavo trip"). One equality read per bus; no index needed. */
  const [pinnedRuns, setPinnedRuns] = useState<PinnedRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [busFilter, setBusFilter] = useState("all");
  const [showIneffectiveOnly, setShowIneffectiveOnly] = useState(false);
  // Deep-link target: the "review your routes" email links to ?route=<id>.
  const [selectedRoute, setSelectedRoute] = useState<string | null>(() => {
    try { return new URLSearchParams(window.location.search).get("route"); } catch { return null; }
  });
  const [driverNames, setDriverNames] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!fleetOrgCode) { setLoading(false); return; }
    // Safety cap so this can't grow into a runaway read as routes accumulate.
    // No orderBy (would need a composite index); 300 is far above any real org's
    // learned-route count for the pilot horizon.
    const q = query(collection(db, "busRouteProfiles"), where("orgCode", "==", fleetOrgCode), limit(300));
    const unsub = onSnapshot(q, (snap) => { setProfiles(snap.docs.map((d) => ({ id: d.id, ...d.data() } as RouteProfileDoc))); setLoading(false); }, () => setLoading(false));
    // The road engine's output — which roads each bus actually drove, per side.
    const rq = query(collection(db, "busRoads"), where("orgCode", "==", fleetOrgCode), limit(100));
    const unsubRoads = onSnapshot(rq, (snap) => setRoadDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() } as BusRoadsDoc))), () => setRoadDocs([]));
    return () => { unsub(); unsubRoads(); };
  }, [fleetOrgCode]);

  /* The window's runs: one read per bus on the index the bus page already uses
     (schoolCode + busId + routeStartedAt desc) — no new index, no listener,
     and nothing at all while the window is "all time". 200 runs a bus covers
     a school year of daily running. */
  const wantRuns = windowKey !== "all";
  const busIdsKey = buses.map((b) => b.id).join(",");
  useEffect(() => {
    if (!fleetOrgCode || !wantRuns || !busIdsKey) { setRuns([]); return; }
    let cancelled = false;
    (async () => {
      setRunsLoading(true);
      try {
        const all: RunLite[] = [];
        for (const bid of busIdsKey.split(",")) {
          const snap = await getDocs(query(
            collection(db, "busAttendance"),
            where("schoolCode", "==", fleetOrgCode),
            where("busId", "==", bid),
            orderBy("routeStartedAt", "desc"),
            limit(200),
          ));
          snap.docs.forEach((d) => { const r = runLiteFromDoc(d.id, d.data() as Record<string, unknown>); if (r) all.push(r); });
        }
        if (!cancelled) setRuns(all);
      } catch {
        if (!cancelled) setRuns([]);
      } finally {
        if (!cancelled) setRunsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fleetOrgCode, wantRuns, busIdsKey]);

  useEffect(() => {
    if (!fleetOrgCode || !busIdsKey) { setPinnedRuns([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const out: PinnedRun[] = [];
        for (const bid of busIdsKey.split(",")) {
          const snap = await getDocs(query(
            collection(db, "busAttendance"),
            where("schoolCode", "==", fleetOrgCode),
            where("busId", "==", bid),
            where("pinned", "==", true),
            limit(50),
          ));
          snap.docs.forEach((d) => {
            const data = d.data() as Record<string, unknown>;
            const ts = (v: unknown): Date | null => (v && typeof (v as Timestamp).toDate === "function" ? (v as Timestamp).toDate() : null);
            const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
            out.push({
              id: d.id,
              busId: bid,
              name: typeof data.pinnedName === "string" && data.pinnedName ? data.pinnedName : "Pinned run",
              date: ts(data.routeStartedAt) ?? ts(data.date),
              type: typeof data.routeType === "string" ? data.routeType : "",
              km: num(data.distanceKm),
              minutes: num(data.durationMinutes),
            });
          });
        }
        out.sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));
        if (!cancelled) setPinnedRuns(out);
      } catch {
        if (!cancelled) setPinnedRuns([]);
      }
    })();
    return () => { cancelled = true; };
  }, [fleetOrgCode, busIdsKey]);

  // Resolve driver ids → names for the per-driver route breakdown (driverDurations).
  useEffect(() => {
    if (!fleetOrgCode) return;
    (async () => {
      try {
        const docs = await getOrgUserDocs(fleetOrgCode);
        const m: Record<string, string> = {};
        docs.forEach((d) => { const u = d.data() as UserDoc; m[d.id] = u.displayName || u.name || u.email || d.id; });
        setDriverNames(m);
      } catch { /* names just won't resolve */ }
    })();
  }, [fleetOrgCode]);

  const busMap = useMemo(() => { const m: Record<string, string> = {}; buses.forEach((b) => { m[b.id] = b.name || b.plate; }); return m; }, [buses]);
  const credible = useMemo(() => profiles.filter(isCredibleRoute), [profiles]);
  const hiddenJunk = profiles.length - credible.length;
  const learned = useMemo(() => credible.filter((p) => p.isLearned), [credible]);
  const provisional = useMemo(
    () => credible.filter((p) => !p.isLearned && p.tripCount >= 1).sort((a, b) => b.tripCount - a.tripCount || a.effectivenessScore - b.effectivenessScore),
    [credible],
  );
  const applyBusFilter = (list: RouteProfileDoc[]) =>
    busFilter === "all" ? list : list.filter((p) => p.busId === busFilter);
  const filtered = useMemo(() => {
    let list = learned;
    if (busFilter !== "all") list = list.filter((p) => p.busId === busFilter);
    if (showIneffectiveOnly) list = list.filter((p) => p.effectivenessScore < 60);
    return [...list].sort((a, b) => routeSide(a).localeCompare(routeSide(b)) || a.effectivenessScore - b.effectivenessScore);
  }, [learned, busFilter, showIneffectiveOnly]);
  const filteredProvisional = useMemo(() => applyBusFilter(provisional), [provisional, busFilter]);
  /* LEARNED routes only — on the map and in the tables. Gabriel, 2026-08-22,
     after the Routes tab listed every 1-of-3 and 2-of-3 cluster beside the
     real ones: "it's showing trips instead of routes. Routes are what is
     learned, 3 of 3. Every trip is already under Recent runs on the bus."
     Still-learning clusters are counted in one line below, never listed. */
  const mapProfiles = useMemo(
    () => applyBusFilter(learned).filter((p) => routeCoords(p) !== null),
    [learned, busFilter],
  );

  const avgEff = learned.length ? Math.round(learned.reduce((a, p) => a + p.effectivenessScore, 0) / learned.length) : 0;
  const flagged = learned.filter((p) => (p.flags ?? []).length > 0).length;
  const avgTime = learned.length ? Math.round(learned.reduce((a, p) => a + p.avgDurationMinutes, 0) / learned.length) : 0;
  const withKm = learned.filter((p) => (p.avgDistanceKm ?? 0) > 0);
  const avgKm = withKm.length
    ? withKm.reduce((a, p) => a + (p.avgDistanceKm ?? 0), 0) / withKm.length
    : 0;
  const routeLabel = (p: RouteProfileDoc) => routeDisplayName(p);
  const selectedProfile = useMemo(
    () => credible.find((p) => p.id === selectedRoute) ?? null,
    [credible, selectedRoute],
  );

  const win = useMemo(
    () => windowBounds(windowKey, { termStartYmd: activeTermStartYmd(termStartDate), termEndYmd: termEndDate }),
    [windowKey, termStartDate, termEndDate],
  );
  const windowed = win.key !== "all";
  /* Anchors are the profile's real endpoints only. The routeHash fallback in
     routeCoords() gives a 1.1 km cell, which would reject every honest run at
     the 150 m tolerance; a legacy profile without endpoints matches on bus and
     side instead. */
  const statsFor = useMemo(() => {
    const m = new Map<string, WindowStats>();
    if (!windowed) return m;
    const ok = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);
    for (const p of credible) {
      const anchored = ok(p.startLat) && ok(p.startLng) && ok(p.endLat) && ok(p.endLng);
      m.set(p.id, windowStats(runs, {
        busId: p.busId,
        side: routeSide(p),
        start: anchored ? { lat: p.startLat as number, lng: p.startLng as number } : null,
        end: anchored ? { lat: p.endLat as number, lng: p.endLng as number } : null,
      }, win));
    }
    return m;
  }, [windowed, credible, runs, win]);
  const runsInWindow = useMemo(() => learned.reduce((a, p) => a + (statsFor.get(p.id)?.runs ?? 0), 0), [learned, statsFor]);
  const canPickTerm = !!activeTermStartYmd(termStartDate);

  const renderRouteRow = (p: RouteProfileDoc) => {
    const st = windowed ? statsFor.get(p.id) : undefined;
    const quietInWindow = windowed && (!st || st.runs === 0);
    const [cls, lbl] = scoreBadge(p.effectivenessScore);
    const dot = p.isLearned ? routeHealth(p.effectivenessScore).color : "#9ca3af";
    const scoreCell = !p.isLearned
      ? (p.tripCount >= 2
          ? <b style={{ color: routeHealth(p.effectivenessScore).color }}>{p.effectivenessScore}</b>
          : <span title="One trip — nothing to compare against yet">—</span>)
      : <span className={`score-badge ${cls}`}><b>{p.effectivenessScore}</b> {lbl}</span>;
    return (
      <tr
        key={p.id}
        onClick={() => setSelectedRoute(p.id)}
        className={selectedRoute === p.id ? "rt-row-sel" : ""}
        style={{ cursor: "pointer", opacity: isStaleRoute(p) || quietInWindow ? 0.55 : 1 }}
      >
        <td><span className="dotcell" style={{ background: dot, opacity: p.isLearned ? 1 : 0.65 }} /></td>
        <td className="name">
          {routeLabel(p)}
          {isStaleRoute(p) && <span className="rt-stale">stale</span>}
          {quietInWindow && p.isLearned && <span className="rt-stale">no runs {win.label}</span>}
          {!p.isLearned && <span className="rt-stale">{p.tripCount}/{LEARNED_AFTER_TRIPS}</span>}
        </td>
        <td>{busMap[p.busId] ?? p.busId}</td>
        <td>{windowed ? (st && st.avgMinutes != null ? formatMinutes(st.avgMinutes) : "—") : formatMinutes(p.avgDurationMinutes)}</td>
        <td>{windowed ? (st && st.avgKm != null ? formatRouteKm(st.avgKm) : "—") : formatRouteKm(p.avgDistanceKm)}</td>
        <td>{windowed ? (st ? st.runs : 0) : p.tripCount}</td>
        <td>{scoreCell}</td>
        <td>{(p.flags ?? []).length ? (p.flags ?? []).map((f, i) => <span key={i} className={`flagpill ${/incident/i.test(f) ? "bad" : "warn"}`}>{f}</span>) : <span style={{ color: "var(--ink-3)" }}>None</span>}</td>
      </tr>
    );
  };

  if (loading) return <div className="an-loading">Loading routes…</div>;

  return (
    <>
      <div className="infobox"><b>How the score works:</b> Every route starts with 100 points. It loses points when something goes wrong on a trip (an incident), when trips suddenly take longer than they usually do, when they keep getting slower week after week, or when the time jumps around a lot from day to day. No problems — it stays at 100. The score updates after every finished run. A route appears here after 3 matching runs (same bus, same direction), and counts as confirmed after 8.</div>
      {hiddenJunk > 0 && (
        <div className="infobox" style={{ background: "#fefce8", borderColor: "#fde68a", color: "#854d0e" }}>
          <b>{hiddenJunk} desk-test route{hiddenJunk === 1 ? "" : "s"} hidden</b> — short trips under {MIN_ROUTE_LEARN_KM} km are not shown here. Recent runs on the bus page still lists every trip. Route stats refresh when the next trip finishes or the nightly sync runs.
        </div>
      )}
      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">Learned routes</div><div className="kpi-v">{learned.length}</div><div className="kpi-d flat">across {buses.length} buses</div></div>
        {windowed && (
          <div className="kpi"><div className="kpi-k">Runs {win.label}</div><div className="kpi-v">{runsLoading ? "…" : runsInWindow}</div><div className="kpi-d flat">on learned routes</div></div>
        )}
        <div className="kpi"><div className="kpi-k">Avg effectiveness</div><div className="kpi-v">{avgEff || "—"}</div><div className="kpi-d flat">0–100 scale</div></div>
        <div className="kpi"><div className="kpi-k">Flagged routes</div><div className="kpi-v" style={{ color: flagged ? "var(--amber)" : undefined }}>{flagged}</div><div className="kpi-d flat">need attention</div></div>
        <div className="kpi"><div className="kpi-k">Avg trip time</div><div className="kpi-v">{avgTime || "—"}<small> min</small></div><div className="kpi-d flat">learned routes</div></div>
        {/* Distance was written into every route profile as `avgDistanceKm` from
            the day route intelligence shipped, and read by nothing — so the one
            number an owner needs to sanity-check a route (and the one Gabriel
            could not find anywhere in the product, 2026-08-01) was being
            computed and thrown away. Legacy profiles predate the field, hence
            the ?? 0 and the em-dash rather than a confident 0.0 km. */}
        <div className="kpi"><div className="kpi-k">Avg distance</div><div className="kpi-v">{avgKm ? avgKm.toFixed(1) : "—"}<small> km</small></div><div className="kpi-d flat">per learned route</div></div>
      </div>

      {pinnedRuns.length > 0 && (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="card-head"><div className="card-title">Pinned runs</div><div className="card-note">named on the bus page · Recent runs · 📌</div></div>
          <div className="scrolltbl">
            <table className="tbl">
              <thead><tr><th>Run</th><th>Bus</th><th>Date</th><th>Type</th><th>Distance</th><th>Time</th></tr></thead>
              <tbody>
                {pinnedRuns.map((r) => (
                  <tr key={r.id} onClick={() => onOpenRun?.(r.busId, r.id)} style={{ cursor: onOpenRun ? "pointer" : "default" }} title={onOpenRun ? "Open this run on the bus page" : undefined}>
                    <td className="name">📌 {r.name}</td>
                    <td>{busMap[r.busId] ?? r.busId}</td>
                    <td>{r.date ? r.date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td style={{ textTransform: "capitalize" }}>{r.type.replace(/_/g, " ") || "—"}</td>
                    <td>{formatRouteKm(r.km)}</td>
                    <td>{r.minutes ? formatMinutes(r.minutes) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredProvisional.length > 0 && (
        <div className="infobox" style={{ marginBottom: 12 }}>
          <b>{filteredProvisional.length} route{filteredProvisional.length === 1 ? "" : "s"} still learning</b> — a route appears here after {LEARNED_AFTER_TRIPS} matching trips on the same morning or evening run. Every trip is already listed under Recent runs on the bus page.
        </div>
      )}

      {learned.length === 0 ? (
        <div className="card card-pad">
          <div className="an-empty">
            <div className="t">No learned routes yet</div>
            Finish {LEARNED_AFTER_TRIPS} matching runs — morning, evening or a planned school trip to the same place (about {MIN_ROUTE_LEARN_KM} km each) — and the route appears here once it is learned.
          </div>
        </div>
      ) : (
        <>
          {mapProfiles.length > 0 && (
          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="card-head"><div className="card-title">Route map</div><div className="card-note">tap a pin to focus a route · learned routes only</div></div>
            <RoutesMap profiles={mapProfiles} selectedId={selectedRoute} onSelect={setSelectedRoute} />
            {selectedProfile && (() => { const h = routeHealth(selectedProfile.effectivenessScore); return (
              <div className="rt-selected" style={{ borderLeft: `4px solid ${h.color}` }}>
                <div className="rt-selected-top">
                  <span className="rt-selected-name">{routeLabel(selectedProfile)}</span>
                  <span className="rt-selected-score" style={{ color: h.color }}>
                    {selectedProfile.isLearned
                      ? `${selectedProfile.effectivenessScore} · ${h.label}`
                      : `${selectedProfile.tripCount}/${LEARNED_AFTER_TRIPS} trips · learning`}
                  </span>
                </div>
                <div className="rt-selected-hint">
                  {formatMinutes(selectedProfile.avgDurationMinutes)} avg · {formatRouteKm(selectedProfile.avgDistanceKm)}
                  {isStaleRoute(selectedProfile) ? " · No recent trips — score may be out of date." : ""}
                </div>
                {windowed && (() => {
                  const st = statsFor.get(selectedProfile.id);
                  if (!st) return null;
                  return (
                    <div className="rt-selected-hint">
                      {st.runs} run{st.runs === 1 ? "" : "s"} {win.label}
                      {st.avgMinutes != null ? ` · ${formatMinutes(st.avgMinutes)} avg` : ""}
                      {st.avgKm != null ? ` · ${formatRouteKm(st.avgKm)} avg` : ""}
                    </div>
                  );
                })()}
                <div className="rt-selected-hint">{h.hint}</div>
                {(selectedProfile.flagReasons ?? []).length > 0 && (
                  <ul className="rt-selected-reasons">{(selectedProfile.flagReasons ?? []).map((r, i) => <li key={i}>{r}</li>)}</ul>
                )}
                {(selectedProfile.recentDurations ?? []).length >= 2 && (
                  <div className="rt-selected-spark">
                    <span className="rt-spark-label">Recent trip times (min)</span>
                    <Sparkline values={selectedProfile.recentDurations!} color={h.color} />
                    {/* oldest → newest; the right end is the latest run */}
                    <span className="rt-spark-latest">
                      latest {Math.round(selectedProfile.recentDurations![selectedProfile.recentDurations!.length - 1])}m
                    </span>
                  </div>
                )}
                {selectedProfile.driverDurations && Object.keys(selectedProfile.driverDurations).length > 0 && (
                  <div className="rt-selected-drivers">
                    {Object.entries(selectedProfile.driverDurations).map(([drv, durs]) => {
                      const arr = (durs ?? []).filter((n) => Number.isFinite(n));
                      if (!arr.length) return null;
                      const avg = Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
                      return <span key={drv} className="rt-driver-chip">{(driverNames[drv] ?? drv).slice(0, 16)} · {avg}m · {arr.length} trip{arr.length !== 1 ? "s" : ""}</span>;
                    })}
                  </div>
                )}
              </div>
            ); })()}
          </div>
          )}

          {learned.length > 0 && (
          <div data-zt="routeTable" className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="card-head"><div className="card-title">Lowest scoring routes</div><div className="card-note">effectiveness score · learned only</div></div>
            <div style={{ marginTop: 6 }}>
              <HBars data={[...learned].sort((a, b) => a.effectivenessScore - b.effectivenessScore).slice(0, 5).map((p) => ({ label: routeLabel(p), value: p.effectivenessScore, color: p.effectivenessScore >= 80 ? "#16a34a" : p.effectivenessScore >= 50 ? "#d97706" : "#dc2626" }))} fmt={(v) => `${v}`} />
            </div>
          </div>
          )}

          <div className="filters">
            <select className="select" value={windowKey} onChange={(e) => setWindowKey(e.target.value as RouteWindowKey)} title="Runs, times and distances are counted inside this window; the score is always all-time">
              {canPickTerm && <option value="term">This term</option>}
              <option value="d30">Last 30 days</option>
              <option value="d90">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            <select className="select" value={busFilter} onChange={(e) => setBusFilter(e.target.value)}>
              <option value="all">All buses</option>
              {buses.map((b) => <option key={b.id} value={b.id}>{b.name || b.plate}</option>)}
            </select>
            <label className="checkbox"><input type="checkbox" checked={showIneffectiveOnly} onChange={(e) => setShowIneffectiveOnly(e.target.checked)} />Show only ineffective (&lt; 60)</label>
            <span className="count">{filtered.length} route{filtered.length !== 1 ? "s" : ""}</span>
          </div>

          {ROUTE_SIDES.map((side) => {
            const learnedSide = filtered.filter((p) => routeSide(p) === side);
            if (learnedSide.length === 0) return null;
            return (
              <div key={side} className="rt-side-section">
                <div className="rt-side-head">
                  <span className="rt-side-title">{routeSideLabel(side)} routes</span>
                  <span className="rt-side-note">{learnedSide.length} learned{windowed ? " · runs, time and km are " + win.label + " · score is all-time" : ""}</span>
                </div>
                <div className="card scrolltbl">
                  <table className="tbl">
                    <thead><tr><th></th><th>Route</th><th>Bus</th><th>Avg time</th><th>Avg km</th><th>{windowed ? "Runs" : "Trips"}</th><th>Score</th><th>Flags</th></tr></thead>
                    <tbody>
                      {learnedSide.map((p) => renderRouteRow(p))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── Roads: which roads each bus actually drove, and which cost time ──
          Gabriel's definition of a route (2026-08-18). The table above keys on
          start+end and measures only time; this is the WHY. Whole-trip minutes
          with the stop count beside them, the evidence level stated, and the
          crawl zone that explains the difference. */}
      {roadDocs.filter((r) => busFilter === "all" || r.busId === busFilter).length > 0 && (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="card-head">
            <div className="card-title">Roads driven</div>
            <div className="card-note">a route is the roads actually taken · learned at 2 trips</div>
          </div>
          {roadDocs
            .filter((r) => busFilter === "all" || r.busId === busFilter)
            .sort((a, b) => (a.busId + a.side).localeCompare(b.busId + b.side))
            .map((r) => (
              <div key={r.id} style={{ padding: "10px 0", borderTop: "1px solid var(--line, #eef0f3)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                  <b>{busMap[r.busId] ?? r.busId} · {r.side}</b>
                  <span className="card-note">
                    {r.roads.length} road{r.roads.length === 1 ? "" : "s"} · {r.tripsConsidered} trips
                    {r.evidence === "thin" ? " · thin evidence" : r.evidence === "ok" ? "" : ""}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 13.5, lineHeight: 1.5 }}>{r.sentence}</div>
                {r.roads.length > 1 && (
                  <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                    {r.roads.map((rd, rdIdx) => (
                      <div key={rd.roadId} className="card-note" style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span className="dotcell" style={{ background: rd.roadId === r.fastestRoadId ? "#16a34a" : rd.roadId === r.slowestRoadId ? "#ea580c" : "#9ca3af" }} />
                        {/* The ACTUAL road (reverse-geocoded server-side, cached on
                            the doc). Until the sweep has named it: Road 1, 2… in
                            fastest-first order — same order as the dots. */}
                        <span style={{ fontWeight: 600 }}>{rd.name || `Road ${rdIdx + 1}`}</span>
                        <span>{rd.avgMinutes} min · {rd.avgStops} stops · {rd.trips} trip{rd.trips === 1 ? "" : "s"}{rd.isLearned ? "" : " · not yet learned"}</span>
                        {rd.crawlZones[0] && <span>· crawls ~{Math.round(rd.crawlZones[0].avgSeconds / 60)} min{rd.crawlZones[0].hourLocal != null ? ` around ${rd.crawlZones[0].hourLocal}:00` : ""}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </>
  );
}

// ─── DRIVERS TAB ────────────────────────────────────────────────────────────
/** Exported for the Trasse Families console. Note for that caller: a household
 *  has ONE driver and no fuel dimension (Families fuel is event-only, so
 *  fuelScore never moves off 100) — hide that bar rather than showing a
 *  perfect score the product cannot stand behind. */
export function DriversTab({ fleetOrgCode }: { fleetOrgCode: string | null }) {
  const [ratings, setRatings] = useState<DriverRatingDoc[]>([]);
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fleetOrgCode) { setLoading(false); return; }
    const q = query(collection(db, "driverRatings"), where("orgCode", "==", fleetOrgCode), limit(300));
    const unsub = onSnapshot(q, (snap) => { setRatings(snap.docs.map((d) => ({ id: d.id, ...d.data() } as DriverRatingDoc))); setLoading(false); }, () => setLoading(false));
    return () => unsub();
  }, [fleetOrgCode]);

  useEffect(() => {
    if (!fleetOrgCode) return;
    (async () => {
      try {
        const docs = await getOrgUserDocs(fleetOrgCode);
        setUsers(docs.map((d) => ({ id: d.id, ...d.data() } as UserDoc)).filter((u) => u.role === "driver" || u.role === "bus_driver"));
      } catch { /* names just won't resolve */ }
    })();
  }, [fleetOrgCode]);

  const userMap = useMemo(() => { const m: Record<string, string> = {}; users.forEach((u) => { m[u.id] = u.displayName || u.name || u.email || u.id; }); return m; }, [users]);
  /* Trip-conductor logins (uid `tripc_…`) are throwaway school-trip crew
     accounts, not drivers — one ran a trip and topped the podium as
     "tripc_g1lwRxU…" (Gabriel, 2026-08-24: "who is that driver??"). */
  const sorted = useMemo(() => ratings.filter((r) => !String(r.driverId ?? r.id).startsWith("tripc_")).sort((a, b) => (b.stars ?? 0) - (a.stars ?? 0)), [ratings]);
  /* The row Zana means when she says "your lowest-rated driver": the worst
     score with enough trips behind it to be worth saying out loud. Mirrors
     `lowestRatedDriver` in the engine's takeover.js. */
  const zanaFocusRowId = useMemo(() => {
    /* Mirrors lowestRatedDriver() in zana/engine/takeover.js — and it did not,
       in three ways, each of which could glow a different person from the one
       she names aloud (review, 09-06):
         • the engine requires `stars > 0`; this did not, so an UNRATED driver
           could sit last and take the highlight while she named somebody else;
         • the engine reads `windowTrips || totalTrips`, this read
           `totalTrips ?? windowTrips` — and `0 ?? 5` is 0 while `0 || 5` is 5,
           so a driver with recent trips but no lifetime count was excluded here
           and included there;
         • when nobody qualifies the engine names NOBODY, while this fell back
           to the whole table and glowed a row regardless.
       `sorted` is stars-descending, so the lowest qualifying star sits last. */
    const rated = sorted.filter((d) => Number.isFinite(Number(d.stars))
      && Number(d.stars) > 0
      && Number(d.windowTrips || d.totalTrips || 0) > 0);
    return rated.length ? rated[rated.length - 1].id : null;
  }, [sorted]);
  const medal = ["🥇", "🥈", "🥉"];
  const mb = (v: number) => { const c = v >= 70 ? "#16a34a" : v >= 45 ? "#d97706" : "#dc2626"; return <div className="minibar-wrap"><span className="minibar"><div style={{ width: `${v}%`, background: c }} /></span><b>{v}</b></div>; };

  if (loading) return <div className="an-loading">Loading driver data…</div>;
  if (sorted.length === 0) return <div className="card card-pad"><div className="an-empty"><div className="t">No driver ratings yet</div>Ratings appear automatically as drivers complete trips.</div></div>;

  return (
    <>
      <div className="podium">
        {sorted.slice(0, 3).map((d, i) => (
          <div className={`pod${i === 0 ? " first" : ""}`} key={d.id}>
            <div className="pod-rank">#{i + 1}</div><div className="pod-medal">{medal[i]}</div>
            <div className="pod-av" style={{ background: BUS_COLORS[i % BUS_COLORS.length] }}>{(userMap[d.driverId] ?? d.driverId).slice(0, 2).toUpperCase()}</div>
            <div className="pod-name">{userMap[d.driverId] ?? d.driverId}</div>
            <div className="pod-role">Driver</div>
            <div className="pod-stars stars">{starString(d.stars ?? 0)}</div>
            <div className="pod-score"><b>{(d.stars ?? 0).toFixed(1)}</b> / 5.0{d.totalTrips != null ? ` · ${d.totalTrips} trips` : ""}</div>
          </div>
        ))}
      </div>
      <div className="card scrolltbl" data-zt="driverTable">
        <table className="tbl">
          <thead><tr><th>Rank</th><th>Driver</th><th>Stars</th><th>Safety</th><th>Fuel</th><th title="Share of recent trips that were GPS-tracked — keeping tracking on">Coverage</th><th title="On-time vs the route's learned duration">Time</th><th>Trips</th><th>Flags</th></tr></thead>
          <tbody>
            {sorted.map((d, i) => (
              /* `driverRow` marks the driver Zana NAMES — the lowest-rated one
                 with trips behind them. The table is sorted best-first, so her
                 narration ("your lowest-rated driver … top of the table I have
                 lit up") pointed the owner's eye at the BEST driver while
                 describing the worst. `driverTable` glows the whole table and
                 cannot disambiguate, so the row needs its own target. */
              <tr key={d.id} data-zt={d.id === zanaFocusRowId ? 'driverRow' : undefined}>
                <td style={{ fontWeight: 700, color: "var(--ink-3)" }}>{i < 3 ? medal[i] : `#${i + 1}`}</td>
                <td className="name">{userMap[d.driverId] ?? d.driverId}{(d.windowTrips ?? 0) > 0 && (d.windowTrips ?? 0) < 10 && <span className="rt-stale" title="Fewer than 10 recent trips — score still settling">new</span>}</td>
                <td><span className="stars">{starString(d.stars ?? 0)}</span> <span style={{ color: "var(--ink-3)", fontSize: 11 }}>{(d.stars ?? 0).toFixed(1)}</span></td>
                <td>{mb(d.safetyScore ?? 0)}</td><td>{mb(d.fuelScore ?? 0)}</td><td>{mb(d.coverageScore ?? d.complianceScore ?? 0)}</td><td>{mb(d.timeScore ?? 0)}</td>
                <td>{d.totalTrips ?? "—"}</td>
                <td>{(d.activeIncidentFlags ?? 0) > 0 ? <span className="flagpill warn">⚠ {d.activeIncidentFlags}</span> : <span style={{ color: "var(--ink-3)" }}>None</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── INCIDENTS TAB ──────────────────────────────────────────────────────────
function IncidentsTab({ buses, fleetOrgCode, rangeDays, setRangeDays }: { buses: BusAnalyticsPageProps["buses"]; fleetOrgCode: string | null; rangeDays: number; setRangeDays: (n: number) => void }) {
  const [incidents, setIncidents] = useState<IssueReportDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [busFilter, setBusFilter] = useState("all");

  useEffect(() => {
    if (!fleetOrgCode || buses.length === 0) { setLoading(false); return; }
    const busIds = buses.map((b) => b.id);
    const chunks: string[][] = [];
    // 8 per chunk, not 30: the busIssueReports rule spends one get(buses/{id})
    // per `in` value against a 10-access-call budget whose sharing semantics
    // are undocumented. See the issue-reports loader in BusTracking.tsx (bug 73).
    for (let i = 0; i < busIds.length; i += 8) chunks.push(busIds.slice(i, i + 8));
    const allDocs = new Map<string, IssueReportDoc>();
    const allUnsubs: (() => void)[] = [];
    let resolved = 0;
    chunks.forEach((chunk) => {
      // orgCode filter is required by the rule — see the note in BusTracking.tsx.
      const q = query(
        collection(db, "busIssueReports"),
        where("orgCode", "==", fleetOrgCode),
        where("busId", "in", chunk),
        limit(200),
      );
      const unsub = onSnapshot(q, (snap) => {
        snap.docs.forEach((d) => allDocs.set(d.id, { id: d.id, ...d.data() } as IssueReportDoc));
        resolved++;
        if (resolved >= chunks.length) { setIncidents([...allDocs.values()]); setLoading(false); }
      }, () => { resolved++; if (resolved >= chunks.length) setLoading(false); });
      allUnsubs.push(unsub);
    });
    return () => allUnsubs.forEach((u) => u());
  }, [fleetOrgCode, buses]);

  const busMap = useMemo(() => { const m: Record<string, string> = {}; buses.forEach((b) => { m[b.id] = b.name || b.plate; }); return m; }, [buses]);
  const cutoff = useMemo(() => Date.now() - rangeDays * 24 * 60 * 60 * 1000, [rangeDays]);
  const filtered = useMemo(() => {
    let list = incidents.filter((i) => tsToMs(i.timestamp ?? i.createdAt ?? null) >= cutoff);
    if (busFilter !== "all") list = list.filter((i) => i.busId === busFilter);
    return list.sort((a, b) => tsToMs(b.timestamp ?? b.createdAt ?? null) - tsToMs(a.timestamp ?? a.createdAt ?? null));
  }, [incidents, busFilter, cutoff]);

  const sevKey = (s?: string) => { const x = (s ?? "").toLowerCase(); return x === "critical" ? "high" : x === "high" || x === "medium" || x === "low" ? x : "low"; };
  const sevCounts = useMemo(() => { const c = { high: 0, medium: 0, low: 0 } as Record<string, number>; filtered.forEach((i) => c[sevKey(i.severity)]++); return c; }, [filtered]);
  const byType = useMemo(() => { const m: Record<string, number> = {}; filtered.forEach((i) => { const t = i.issueType || "Other"; m[t] = (m[t] ?? 0) + 1; }); return Object.entries(m).sort(([, a], [, b]) => b - a); }, [filtered]);
  const byBus = useMemo(() => { const m: Record<string, number> = {}; filtered.forEach((i) => { if (i.busId) m[i.busId] = (m[i.busId] ?? 0) + 1; }); return Object.entries(m).sort(([, a], [, b]) => b - a); }, [filtered]);
  const overTime = useMemo(() => {
    const m: Record<string, number> = {};
    filtered.forEach((i) => { const ms = tsToMs(i.timestamp ?? i.createdAt ?? null); if (ms) m[weekKey(ms)] = (m[weekKey(ms)] ?? 0) + 1; });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([, v], idx) => ({ label: `W${idx + 1}`, value: v }));
  }, [filtered]);
  const highPct = filtered.length ? Math.round((sevCounts.high / filtered.length) * 100) : 0;
  const mostBus = byBus.length ? { name: busMap[byBus[0][0]] ?? byBus[0][0], count: byBus[0][1] } : null;
  const recent = filtered.length ? formatDate(tsToMs(filtered[0].timestamp ?? filtered[0].createdAt ?? null)) : null;
  const TYPE_COLORS = ["#dc2626", "#d97706", "#2563eb", "#7c3aed", "#99a1ae"];

  if (loading) return <div className="an-loading">Loading incidents…</div>;

  return (
    <>
      <div className="kpi-row">
        <div className="kpi"><div className="kpi-k">Total incidents</div><div className="kpi-v">{filtered.length}</div><div className="kpi-d flat">selected period</div></div>
        <div className="kpi"><div className="kpi-k">High severity</div><div className="kpi-v" style={{ color: "var(--red)" }}>{sevCounts.high}</div><div className="kpi-d flat">{highPct}% of total</div></div>
        <div className="kpi"><div className="kpi-k">Most incidents</div><div className="kpi-v" style={{ fontSize: 22 }}>{mostBus ? mostBus.name : "—"}</div><div className="kpi-d flat">{mostBus ? `${mostBus.count} incidents` : "none"}</div></div>
        <div className="kpi"><div className="kpi-k">Most recent</div><div className="kpi-v" style={{ fontSize: 22 }}>{recent ?? "—"}</div><div className="kpi-d flat">{filtered[0]?.issueType ?? ""}</div></div>
      </div>

      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="card card-pad">
          <div className="card-head"><div className="card-title">Incidents over time</div><div className="card-note">weekly · last {rangeDays} days</div></div>
          {overTime.length ? <LineChartSVG data={overTime} color="#dc2626" /> : <div className="an-empty">No incidents in range.</div>}
        </div>
        <div className="card card-pad">
          <div className="card-head"><div className="card-title">By severity</div></div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 0 4px" }}>
            <Donut data={[{ value: sevCounts.high, color: "#dc2626" }, { value: sevCounts.medium, color: "#d97706" }, { value: sevCounts.low, color: "#16a34a" }]} center={`${filtered.length}`} sub="incidents" />
          </div>
          <div className="legend" style={{ justifyContent: "center" }}>
            <span><i style={{ background: "#dc2626" }} />High · {sevCounts.high}</span>
            <span><i style={{ background: "#d97706" }} />Medium · {sevCounts.medium}</span>
            <span><i style={{ background: "#16a34a" }} />Low · {sevCounts.low}</span>
          </div>
        </div>
      </div>

      <div className="grid-2b" style={{ marginBottom: 16 }}>
        <div className="card card-pad"><div className="card-head"><div className="card-title">By type</div></div><div style={{ marginTop: 6 }}><HBars data={byType.map(([t, v], i) => ({ label: t, value: v, color: TYPE_COLORS[i % TYPE_COLORS.length] }))} /></div></div>
        <div className="card card-pad"><div className="card-head"><div className="card-title">By bus</div></div><BarChartSVG data={byBus.map(([id, v], i) => ({ label: busMap[id] ?? id, value: v, color: BUS_COLORS[i % BUS_COLORS.length] }))} height={200} valueLabels bw={48} /></div>
      </div>

      <div className="filters">
        <select className="select" value={busFilter} onChange={(e) => setBusFilter(e.target.value)}>
          <option value="all">All buses</option>
          {buses.map((b) => <option key={b.id} value={b.id}>{b.name || b.plate}</option>)}
        </select>
        <select className="select" value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option>
        </select>
        <span className="count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="card card-pad"><div className="an-empty"><div className="t">No incidents in this period</div>Incident reports show here when filed by drivers or admins.</div></div>
      ) : (
        <div className="card scrolltbl">
          <table className="tbl">
            <thead><tr><th>Date</th><th>Bus</th><th>Issue type</th><th>Details</th><th>Severity</th></tr></thead>
            <tbody>
              {filtered.map((inc) => (
                <tr key={inc.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{formatDate(tsToMs(inc.timestamp ?? inc.createdAt ?? null))}</td>
                  <td className="name">{busMap[inc.busId ?? ""] ?? inc.busId ?? "—"}</td>
                  <td>{inc.issueType ?? "—"}</td>
                  <td style={{ color: "var(--ink-2)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inc.details ?? "—"}</td>
                  <td>{inc.severity ? <span className={`severity sev-${sevKey(inc.severity)}`}>{inc.severity}</span> : <span style={{ color: "var(--ink-3)" }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
const SUBTABS: Array<{ key: SubTab; label: string; icon: string; premium?: boolean }> = [
  { key: "fuel", label: "Fuel", icon: "M3 3h11v18H3V3Zm2 2v6h7V5H5Zm12 1 4 4v9a2 2 0 0 1-4 0v-5h-1V6h1Z" },
  { key: "routes", label: "Routes", icon: "M6 3a3 3 0 0 1 1 5.8V14a2 2 0 0 0 2 2h6.2a3 3 0 1 1 0 2H9a4 4 0 0 1-4-4V8.8A3 3 0 0 1 6 3Z" },
  { key: "drivers", label: "Drivers", icon: "m12 2 2.9 6 6.6.6-5 4.3 1.5 6.5L12 16l-5.9 3.4L7.5 13l-5-4.3L9.1 8 12 2Z" },
  { key: "incidents", label: "Incidents", icon: "M5 3h2v18H5V3Zm3 1h11l-2 4 2 4H8V4Z" },
  // internal key stays "advanced"; the user-facing label is "Zana Mode".
  { key: "advanced", label: "Zana Mode", icon: "", premium: true },
];

export default function BusAnalyticsPage({ buses, fleetOrgCode, zanaEnabled = false, onUpgrade, onZanaIntent, liveUnreadAlertCount = 0, subTab = null, subTabNonce = 0, onSubTabChange, currentTermStartDate = null, currentTermEndDate = null, nextTermStartDate = null }: BusAnalyticsPageProps): JSX.Element {
  /* Start on the tab the parent last knew about, not on Fuel. The page can be
     re-mounted underneath the owner (the subscription hook re-emitting, the
     analytics branch re-rendering) — and every re-mount used to flash Fuel for
     a frame before the effect below snapped it back to the tab they were on.
     Gabriel, 2026-08-22: "anytime I go to analytics the thing glitches,
     switching itself between fuel and routes." */
  const [tab, setTab] = useState<SubTab>(subTab ?? "fuel");
  const [rangeDays, setRangeDays] = useState(90);
  // Zana asking for a sub-tab, not a controlled prop: she sets it at the start
  // of a step and the owner stays free to click elsewhere afterwards.
  useEffect(() => {
    if (subTab) setTab(subTab);
  }, [subTab, subTabNonce]);
  useEffect(() => {
    onSubTabChange?.(tab);
  }, [tab, onSubTabChange]);
  // The Zana Mode tab is ALWAYS visible: Premium/Enterprise get the live briefing,
  // Basic gets the upsell "trailer" so they can see what Zana does before upgrading.
  const visibleSubtabs = SUBTABS;

  const liveStart = liveTermStartYmd({
    currentStart: currentTermStartDate,
    nextStart: nextTermStartDate,
  });

  return (
    <div className="pear-analytics">
      <div className="an-head">
        <div>
          <div className="an-title">Fleet Analytics</div>
          <div className="an-sub">{buses.length} bus{buses.length !== 1 ? "es" : ""} · {fleetOrgCode ?? "Unknown org"} · automatically updated</div>
        </div>
        <div className="an-range">
          {tab === "fuel" && liveStart ? (
            <span className="active" style={{ pointerEvents: "none" }}>This term</span>
          ) : (
            [7, 30, 90].map((d) => <button key={d} className={rangeDays === d ? "active" : ""} onClick={() => setRangeDays(d)}>{d}d</button>)
          )}
        </div>
      </div>

      <div className="subtabs">
        {visibleSubtabs.map((t) => (
          <button
            key={t.key}
            className={`${tab === t.key ? "active" : ""}${t.premium ? " subtab-zana" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.key === "advanced" ? <Zana state="online" size={18} animated /> : <Icon d={t.icon} />}
            {t.label}
            {t.premium && <span className="subtab-premium">PREMIUM</span>}
          </button>
        ))}
      </div>

      <div className="an-body">
        {tab === "fuel" && <>{zanaEnabled && <ZanaBand orgCode={fleetOrgCode} tab="fuel" onExecuteIntent={onZanaIntent} liveUnreadAlertCount={liveUnreadAlertCount} />}<FuelTab buses={buses} fleetOrgCode={fleetOrgCode} rangeDays={rangeDays} isZanaTier={!!zanaEnabled} termStartDate={liveStart} termEndDate={currentTermEndDate} /></>}
        {tab === "routes" && <>{zanaEnabled && <ZanaBand orgCode={fleetOrgCode} tab="routes" onExecuteIntent={onZanaIntent} liveUnreadAlertCount={liveUnreadAlertCount} />}<RoutesTab buses={buses} fleetOrgCode={fleetOrgCode} termStartDate={currentTermStartDate} termEndDate={currentTermEndDate} onOpenRun={onZanaIntent ? (busId, runId) => { void onZanaIntent({ actionId: "open_bus", params: { busId, runId } }); } : undefined} /></>}
        {tab === "drivers" && <>{zanaEnabled && <ZanaBand orgCode={fleetOrgCode} tab="drivers" onExecuteIntent={onZanaIntent} liveUnreadAlertCount={liveUnreadAlertCount} />}<DriversTab fleetOrgCode={fleetOrgCode} /></>}
        {tab === "incidents" && <>{zanaEnabled && <ZanaBand orgCode={fleetOrgCode} tab="incidents" onExecuteIntent={onZanaIntent} liveUnreadAlertCount={liveUnreadAlertCount} />}<IncidentsTab buses={buses} fleetOrgCode={fleetOrgCode} rangeDays={rangeDays} setRangeDays={setRangeDays} /></>}
        {tab === "advanced" && (zanaEnabled ? <ErrorBoundary label="Zana Mode"><ZanaModeTab orgCode={fleetOrgCode} onExecuteIntent={onZanaIntent} /></ErrorBoundary> : <ZanaModeUpsell onUpgrade={onUpgrade} plan="basic" />)}
      </div>
    </div>
  );
}
